import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import BluetoothIcon from '@mui/icons-material/Bluetooth';
import BluetoothDisabledIcon from '@mui/icons-material/BluetoothDisabled';
import PrintIcon from '@mui/icons-material/Print';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import usePrinterNative from '../../hooks/usePrinterNative';
import { useSettingsContext } from '../../context/SettingsContext';
import { useTranslation } from 'react-i18next';
import { LANG_TEXT } from '../../constants/language';

const PrinterSettings: React.FC = () => {
  const {
    printerBrand,
    printerTarget,
    isConnected,
    isConnecting,
    isScanning,
    discoveredPrinters,
    bluetoothEnabled,
    scanForPrinters,
    cancelScan,
    clearDiscovered,
    connectPrinter,
    removePrinter,
    printViaIp,
  } = usePrinterNative();

  const { currentSetting } = useSettingsContext();

  const [showScanner, setShowScanner] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const { t } = useTranslation();

  const handleAddPrinter = async () => {
    try {
      if (!bluetoothEnabled) {
        return;
      }
      setShowScanner(true);
      await scanForPrinters();
    } catch (err) {}
  };

  const handleSelectPrinter = async (address: string, brand: 'epson' | 'star') => {
    try {
      if (isScanning && typeof cancelScan === 'function') cancelScan();
    } catch (e) {}
    setShowScanner(false);

    try {
      await connectPrinter(brand, address);
    } catch (err) {}
  };

  const handleRemovePrinter = async () => {
    setShowRemoveDialog(false);

    try {
      await removePrinter();
    } catch (err) {}
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PrintIcon />
        {t(LANG_TEXT.PRINTER_SETTINGS)}
      </Typography>

      {isConnecting || isScanning ? (
        <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ mb: 2 }}>
          {t(LANG_TEXT.SCANNING)}
        </Alert>
      ) : (
        <Alert severity={bluetoothEnabled ? 'info' : 'warning'} icon={bluetoothEnabled ? <BluetoothIcon /> : <BluetoothDisabledIcon />} sx={{ mb: 2 }}>
          {bluetoothEnabled ? t(LANG_TEXT.BLUETOOTH_ENABLED) : t(LANG_TEXT.BLUETOOTH_DISABLED)}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
            {t(LANG_TEXT.DEFAULT_PRINTER)}
          </Typography>

          {printerTarget ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1">{printerBrand ? `${printerBrand.toUpperCase()} ${t(LANG_TEXT.PRINTER_WORD)}` : t(LANG_TEXT.PRINTER_WORD)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {printerTarget}
                </Typography>
              </Box>

              <Chip label={isConnected ? t(LANG_TEXT.CONNECTED) : t(LANG_TEXT.DISCONNECTED)} color={isConnected ? 'success' : 'default'} size="small" sx={{ mr: 2 }} />

              <IconButton color="error" size="small" onClick={() => setShowRemoveDialog(true)} disabled={isConnecting}>
                <DeleteIcon />
              </IconButton>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t(LANG_TEXT.NO_PRINTER_CONFIGURED)}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Button
        variant="contained"
        startIcon={isScanning ? <CircularProgress size={20} color="inherit" /> : <BluetoothIcon />}
        onClick={handleAddPrinter}
        disabled={!bluetoothEnabled || isScanning || isConnecting}
        sx={{ mb: 3 }}
      >
        {isScanning ? t(LANG_TEXT.SCANNING_FOR_PRINTERS) : t(LANG_TEXT.ADD_PRINTER)}
      </Button>

      <Button
        variant="outlined"
        startIcon={<PrintIcon />}
        onClick={async () => {
          try {
            const ip = currentSetting?.ip_printer;
            if (!ip) {
              return;
            }
            await printViaIp(ip);
          } catch (err) {
            console.error(err);
          }
        }}
        sx={{ mb: 3, ml: 2 }}
      >
        {t(LANG_TEXT.PRINT_VIA_IP)}
      </Button>

      <Dialog
        open={showScanner}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setShowScanner(false);
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {t(LANG_TEXT.NEARBY_PRINTERS)}
          <IconButton onClick={handleAddPrinter} disabled={isScanning} size="small">
            <RefreshIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {isScanning && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 4,
                gap: 2,
              }}
            >
              <CircularProgress size={32} />
              <Typography>{t(LANG_TEXT.SCANNING_FOR_PRINTERS)}</Typography>
            </Box>
          )}

          {!isScanning && discoveredPrinters.length === 0 && <Alert severity="info">{t(LANG_TEXT.NO_PRINTERS_FOUND)}</Alert>}

          {!isScanning && discoveredPrinters.length > 0 && (
            <List>
              {discoveredPrinters.map((printer) => (
                <ListItem key={printer.address} disablePadding>
                  <ListItemButton onClick={() => handleSelectPrinter(printer.address, printer.brand)}>
                    <ListItemText primary={printer.name} secondary={`${printer.brand.toUpperCase()} • ${printer.address}`} />
                    <Chip label={printer.brand.toUpperCase()} size="small" />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              try {
                cancelScan?.();
                clearDiscovered?.();
              } catch (e) {}
              setShowScanner(false);
            }}
          >
            {t(LANG_TEXT.CLOSE)}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showRemoveDialog}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setShowRemoveDialog(false);
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>{t(LANG_TEXT.REMOVE_PRINTER_QUESTION)}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography>{t(LANG_TEXT.REMOVE_PRINTER_DESCRIPTION)}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRemoveDialog(false)}>{t(LANG_TEXT.CANCEL)}</Button>
          <Button onClick={handleRemovePrinter} color="error" variant="contained">
            {t(LANG_TEXT.REMOVE)}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {t(LANG_TEXT.PRINTER_SETUP_GUIDE)}
        </Typography>
        <Typography variant="body2" component="div">
          <ol style={{ margin: '8px 0', paddingLeft: 20 }}>
            <li>{t(LANG_TEXT.PRINTER_SETUP_STEP1)}</li>
            <li>{t(LANG_TEXT.PRINTER_SETUP_STEP2)}</li>
            <li>{t(LANG_TEXT.PRINTER_SETUP_STEP3)}</li>
            <li>{t(LANG_TEXT.PRINTER_SETUP_STEP4)}</li>
            <li>{t(LANG_TEXT.PRINTER_SETUP_STEP5)}</li>
          </ol>
        </Typography>
      </Box>
    </Box>
  );
};

export default PrinterSettings;
